import React, { useEffect, useMemo, useRef, useState } from "react";

import { faChevronLeft, faChevronRight } from "@performyard/fontawesome/pro-regular-svg-icons";

import { cn } from "../../../lib/utils";

import { ButtonIcon } from "../../atoms";

export type PaginationProps = {
	pageCount: number;
	currentPage: number;
	onPageChange: (newPage: number) => void;
	dangerouslySetClassName?: string;
};

type PageItem = number | "ellipsis-start" | "ellipsis-end";

const DIGIT_WIDTH = 7; // Width of a single digit character
const BUTTON_PADDING = 8; // Horizontal padding per button (py-1 px-2 = 8px total horizontal)
const GAP_BETWEEN_BUTTONS = 2; // gap-[2px]
const ELLIPSIS_WIDTH = 12; // Width of "..." text (no padding now)
const MIN_WIDTH_THRESHOLD = 100; // Minimum width before showing only current page
const CHEVRON_WIDTH = 32;
const OUTER_GAP = 16;

const getPageButtonWidth = (pageNum: number): number => {
	const digitCount = pageNum.toString().length;
	const contentWidth = digitCount * DIGIT_WIDTH + BUTTON_PADDING * 2;
	return contentWidth;
};

const calculateTotalWidth = (pages: number[]): number => {
	return (
		pages.reduce((total, page) => {
			return total + getPageButtonWidth(page) + GAP_BETWEEN_BUTTONS;
		}, 0) - GAP_BETWEEN_BUTTONS
	);
};

const calculateVisiblePages = (currentPage: number, pageCount: number, containerWidth: number): PageItem[] => {
	// If container is too narrow, only show current page
	if (containerWidth < MIN_WIDTH_THRESHOLD) {
		return [currentPage];
	}

	const availableSpace = containerWidth;

	// If all pages fit, show them all
	const allPages = Array.from({ length: pageCount }, (_, i) => i + 1);
	if (calculateTotalWidth(allPages) <= availableSpace) {
		return allPages;
	}

	// Build pages dynamically based on actual widths
	// Priority: first page, last page, current page, then expand around current

	const requiredPages = new Set<number>([1, pageCount, currentPage]);
	let currentWidth =
		Array.from(requiredPages).reduce((sum, page) => {
			return sum + getPageButtonWidth(page) + GAP_BETWEEN_BUTTONS;
		}, 0) - GAP_BETWEEN_BUTTONS;

	// Account for potential ellipses
	const needsLeftEllipsis = currentPage > 2;
	const needsRightEllipsis = currentPage < pageCount - 1;
	let ellipsisCount = 0;
	if (needsLeftEllipsis) ellipsisCount++;
	if (needsRightEllipsis) ellipsisCount++;

	currentWidth += ellipsisCount * (ELLIPSIS_WIDTH + GAP_BETWEEN_BUTTONS);

	// Try to add siblings around current page
	let leftOffset = 1;
	let rightOffset = 1;
	let canExpandLeft = currentPage - leftOffset > 1;
	let canExpandRight = currentPage + rightOffset < pageCount;

	while (canExpandLeft || canExpandRight) {
		let addedPage = false;

		// Try to add left sibling
		if (canExpandLeft) {
			const leftPage = currentPage - leftOffset;
			const leftPageWidth = getPageButtonWidth(leftPage) + GAP_BETWEEN_BUTTONS;

			// If we're about to bridge the gap to page 1, remove the ellipsis space
			const wouldBridgeLeft = leftPage === 2;
			const adjustedWidth = wouldBridgeLeft
				? leftPageWidth - (ELLIPSIS_WIDTH + GAP_BETWEEN_BUTTONS)
				: leftPageWidth;

			if (currentWidth + adjustedWidth <= availableSpace) {
				requiredPages.add(leftPage);
				currentWidth += adjustedWidth;
				leftOffset++;
				addedPage = true;
				canExpandLeft = currentPage - leftOffset > 1;
			} else {
				canExpandLeft = false;
			}
		}

		// Try to add right sibling
		if (canExpandRight) {
			const rightPage = currentPage + rightOffset;
			const rightPageWidth = getPageButtonWidth(rightPage) + GAP_BETWEEN_BUTTONS;

			// If we're about to bridge the gap to last page, remove the ellipsis space
			const wouldBridgeRight = rightPage === pageCount - 1;
			const adjustedWidth = wouldBridgeRight
				? rightPageWidth - (ELLIPSIS_WIDTH + GAP_BETWEEN_BUTTONS)
				: rightPageWidth;

			if (currentWidth + adjustedWidth <= availableSpace) {
				requiredPages.add(rightPage);
				currentWidth += adjustedWidth;
				rightOffset++;
				addedPage = true;
				canExpandRight = currentPage + rightOffset < pageCount;
			} else {
				canExpandRight = false;
			}
		}

		// If we couldn't add anything this round, stop
		if (!addedPage) break;
	}

	// Build final pages array with ellipses
	const sortedPages = Array.from(requiredPages).sort((a, b) => a - b);
	const pages: PageItem[] = [];

	for (let i = 0; i < sortedPages.length; i++) {
		const page = sortedPages[i];
		const prevPage = sortedPages[i - 1];

		// Add ellipsis if there's a gap
		if (prevPage !== undefined && page - prevPage > 1) {
			pages.push(prevPage < currentPage ? "ellipsis-start" : "ellipsis-end");
		}

		pages.push(page);
	}

	return pages;
};

export const Pagination = ({ pageCount, currentPage, onPageChange, dangerouslySetClassName }: PaginationProps) => {
	const outerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState(0);
	const [outerWidth, setOuterWidth] = useState(0);
	// Calculate total width needed to show all pages (without truncation)
	const totalNeededWidth = useMemo(() => {
		const allPages = Array.from({ length: pageCount }, (_, i) => i + 1);
		const pagesWidth = calculateTotalWidth(allPages);

		// Add chevrons and outer gaps
		return pagesWidth + CHEVRON_WIDTH * 2 + OUTER_GAP * 2;
	}, [pageCount]);

	// Measure container width with ResizeObserver
	useEffect(() => {
		if (!outerRef.current) return;

		const updateWidths = () => {
			if (outerRef.current) {
				const parentWidth = outerRef.current.parentElement?.offsetWidth || 0;
				setOuterWidth(parentWidth);

				// Calculate available space for page numbers (subtract chevrons and gaps)
				const availableSpace = parentWidth - CHEVRON_WIDTH * 2 - OUTER_GAP * 2;
				setContainerWidth(availableSpace);
			}
		};

		// Set initial width immediately
		updateWidths();

		const resizeObserver = new ResizeObserver(() => {
			updateWidths();
		});

		if (outerRef.current.parentElement) {
			resizeObserver.observe(outerRef.current.parentElement);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	// Determine if we need truncation by comparing total needed vs available
	const needsTruncation = outerWidth > 0 && totalNeededWidth > outerWidth;

	const visiblePages = useMemo(
		() =>
			needsTruncation
				? calculateVisiblePages(currentPage, pageCount, containerWidth)
				: Array.from({ length: pageCount }, (_, i) => i + 1),
		[currentPage, pageCount, containerWidth, needsTruncation]
	);

	const backButton = (
		<div className="shrink-0">
			<ButtonIcon
				variant="outline"
				disabled={currentPage === 1}
				icon={faChevronLeft}
				onClick={() => onPageChange(currentPage - 1)}
			/>
		</div>
	);

	const forwardButton = (
		<div className="shrink-0">
			<ButtonIcon
				variant="outline"
				disabled={currentPage === pageCount}
				icon={faChevronRight}
				onClick={() => onPageChange(currentPage + 1)}
			/>
		</div>
	);

	return (
		<div
			ref={outerRef}
			data-truncated={needsTruncation}
			data-visible-pages={visiblePages.length}
			data-total-pages={pageCount}
			aria-label="Pagination"
			className={cn(
				`inline-flex w-fit items-center justify-center gap-4 text-paragraph-3 font-regular data-[truncated=true]:w-full`,
				dangerouslySetClassName
			)}
		>
			{backButton}
			<div className="flex flex-nowrap items-center justify-center gap-0.5">
				{visiblePages.map((item) => {
					if (item === "ellipsis-start" || item === "ellipsis-end") {
						return (
							<span key={item} className="pointer-events-none shrink-0 select-none py-1 text-center">
								...
							</span>
						);
					}

					const page = item as number;
					return (
						<button
							key={page}
							onClick={() => onPageChange(page)}
							aria-current={page === currentPage ? "page" : undefined}
							data-selected={page === currentPage}
							className={cn(
								"shrink-0 cursor-pointer rounded-[3px] border-none bg-transparent px-2 py-1 text-center data-[selected=true]:bg-neutral-1"
							)}
						>
							{page}
						</button>
					);
				})}
			</div>
			{forwardButton}
		</div>
	);
};

Pagination.displayName = "Pagination";
